import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

/**
 * Persists a dismissed / closed flag in `localStorage` under `storageKey`.
 * Writes the literal `'closed'` when dismissed, or `null` when cleared.
 * The stored value is only treated as authoritative after mount so SSR markup matches the initial client render.
 */
export function useLocalStorageClosedState(
  storageKey: string
): [boolean, (isClosed: boolean) => void] {
  const [storageValue, setStorageValue] = useLocalStorage<string | null>(storageKey, null);

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const setIsClosed = useCallback(
    (isClosed: boolean) => {
      setStorageValue(isClosed ? 'closed' : null);
    },
    [setStorageValue]
  );

  return [hasMounted && storageValue === 'closed', setIsClosed];
}
